/**
 * GuestTaskPage — the external contractor's view of one work order (ärende).
 *
 * Renders outside the app providers (no contexts, no user names) on the shared
 * context-free leaves in the canonical section order of
 * `src/Pages/WorkOrders/workOrderSections.ts`, so it reads exactly like the
 * admin `WorkOrderDetailModal`. Reached from the portal or a project page under
 * the user's personal link; the work order is named by `workOrderKey`.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Container } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import logoWhite from '../../Images/logo_white.png';
import './GuestTaskPage.css';
import { useGuestProject } from './hooks/useGuestProject';
import { useCompletionDraft } from '../../hooks/workOrders/useCompletionDraft';
import { useGuestThumbnail } from './hooks/useGuestThumbnail';
import { useGuestPhotoItems } from './hooks/useGuestPhotoItems';
import { WorkOrderViewData, GuestWorkOrderState, ConditionUpdatePayload, PhotoType } from '../../types/guestAccess';
import { WorkOrderPriority, WorkOrderStatus } from '../../types/workOrders';
import { PhotoModel } from '../../types/protocols';
import { Attachment, isImageAttachment } from '../../types/attachments';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { ImageSize } from '../../Helpers/ImageLoader';
import { WORK_ORDER_LABELS as L } from '../WorkOrders/workOrderSections';
import {
    WorkOrderHeader,
    WorkOrderBadges,
    WorkOrderTop,
    usePhotoViewer,
    isWorkOrderOverdue,
    BuildingCard,
    DetailsList,
    IssuePhotosSection,
    AttachmentsList,
    DocumentationSection,
    NotesSection,
    ChecklistsSection,
    PhotoGroup,
    PhotoGroupItem,
    AttachmentRow,
    CompletionForm,
    CompletionSummary,
    AutosaveIndicator,
    CompletionRules,
    ReportedIssuesSection,
} from '../WorkOrders/components/shared';
import GuestComponentLocations from './components/GuestComponentLocations';
import GuestReportIssueModal from './components/GuestReportIssueModal';
import { useGuestReportIssue } from './hooks/useGuestReportIssue';
import { GuestLinkRefusalPage } from './components/GuestLinkStatePages';
import PhotoViewerModal from '../../Components/PhotoViewerModal';

interface GuestTaskPageProps {
    token: string;
    initialData: WorkOrderViewData;
    workOrderKey: string;
    /** Link back to where the work order was opened from (portal or project). */
    back?: { href: string; label: string };
}

const AFTER_PHOTOS_ID = 'wo-after-photos';

const PRIORITIES: readonly string[] = Object.values(WorkOrderPriority);
const toPriority = (value?: string): WorkOrderPriority =>
    value && PRIORITIES.includes(value) ? (value as WorkOrderPriority) : WorkOrderPriority.MEDIUM;

const toStatus = (state: GuestWorkOrderState): WorkOrderStatus =>
    state === 'completed' ? WorkOrderStatus.COMPLETED : WorkOrderStatus.OPEN;

const GuestTaskPage: React.FC<GuestTaskPageProps> = ({ token, initialData, workOrderKey, back }) => {
    const {
        project, building, externalUserId, workOrderContext, workOrderCompleted,
        error, refusal, clearError, submitting,
        completeWorkOrder,
        addNote, updateNote, deleteNote,
        createChecklist, addChecklistItem, setChecklistItemCompleted, deleteChecklistItem, deleteChecklist,
        uploadPhoto, deletePhoto, uploadAttachment, deleteAttachment, getDownloadUrl,
        canDelete,
    } = useGuestProject(token, initialData, { workOrderKey });

    const wo = workOrderContext;
    const currentUid = externalUserId || '';
    const isOpen = wo?.status === 'open' && !workOrderCompleted;
    const sections = wo?.enabledSections || {};

    // --- Completion draft (localStorage, scoped per external user + work order) ---
    // Not keyed by the token: a replaced link must not lose a half-written
    // completion, and the bearer token never lands in storage.
    const draft = useCompletionDraft(isOpen && currentUid ? `guestCompletionDraft:${currentUid}:${workOrderKey}` : null);
    const [completionError, setCompletionError] = useState<string | null>(null);
    const [localCompletion, setLocalCompletion] = useState<{ completedAt: string; comment?: string; conditions?: Record<string, ConditionUpdatePayload> } | null>(null);
    const submittingRef = useRef(false); // double-submit guard

    // --- Photos / attachments ---
    const afterPhotos = useGuestPhotoItems(project?.afterPictures, getDownloadUrl, canDelete);
    const beforePhotos = useGuestPhotoItems(project?.beforePictures, getDownloadUrl, canDelete);
    const progressPhotos = useGuestPhotoItems(project?.progressPictures, getDownloadUrl, canDelete);
    const issuePhotos = useGuestPhotoItems(wo?.sourcePhotos, getDownloadUrl, canDelete);
    const photoInputs = useRef<Partial<Record<PhotoType, HTMLInputElement | null>>>({});

    // --- Felanmäl (#1650): the guest sees only a count of their own reports ---
    const [ownReportCount, setOwnReportCount] = useState(initialData.ownReportCount ?? 0);
    const [showReportIssue, setShowReportIssue] = useState(false);
    const reportIssue = useGuestReportIssue(token, workOrderKey);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [attachmentError, setAttachmentError] = useState<string | undefined>();

    const coverPhotoPath = building?.coverPhoto?.storagePath || '';
    const resolveCoverPhoto = useCallback(
        (path: string) => (coverPhotoPath ? getDownloadUrl(path) : Promise.reject(new Error('no cover photo'))),
        [coverPhotoPath, getDownloadUrl],
    );
    const coverPhoto = useGuestThumbnail(coverPhotoPath, resolveCoverPhoto, ImageSize.SCALED_MEDIUM);

    // Photos open in the viewer, stepping through their own group (as on the admin modal)
    const photoViewer = usePhotoViewer();
    const { open: openViewer } = photoViewer;
    const openPhoto = useCallback((items: PhotoGroupItem[], photos: { [key: string]: PhotoModel } | undefined) => (key: string) => {
        openViewer(items.map((p) => p.key), key, async (k) => {
            const photo = photos?.[k];
            if (!photo) throw new Error('Bilden finns inte');
            return getDownloadUrl(photo.storagePath);
        });
    }, [getDownloadUrl, openViewer]);

    const attachmentRows: AttachmentRow[] = useMemo(() => Object.values((project?.attachments || {}) as Record<string, Attachment>).map((a) => ({
        key: a.key,
        name: a.fileName || a.key,
        sizeBytes: a.fileSize,
        uploadedAt: isImageAttachment(a) ? a.date : a.uploadedAt,
        canRemove: isOpen && canDelete(isImageAttachment(a) ? a.author : a.uploadedBy),
    })), [project?.attachments, isOpen, canDelete]);

    const handleUploadAttachments = async (files: File[]) => {
        setUploadingAttachments(true);
        setAttachmentError(undefined);
        try {
            for (const file of files) await uploadAttachment(file);
        } catch (err) {
            setAttachmentError(err instanceof Error ? err.message : 'Kunde inte ladda upp bilagan');
        } finally {
            setUploadingAttachments(false);
        }
    };
    const handleOpenAttachment = async (key: string) => {
        const a = (project?.attachments as Record<string, Attachment> | undefined)?.[key];
        if (!a) return;
        try { window.open(await getDownloadUrl(a.storagePath), '_blank', 'noopener,noreferrer'); } catch { /* error surfaced by the hook */ }
    };
    const handlePhotoFiles = async (type: PhotoType, files: FileList | null) => {
        if (!files) return;
        for (const file of Array.from(files)) {
            try { await uploadPhoto(file, type); } catch { /* hook sets error */ }
        }
    };

    // --- Completion ---
    const componentKeys = wo?.componentKeys ?? [];
    const rules: CompletionRules = {
        isOpen,
        requiresPhoto: wo?.requiresPhoto ?? false,
        requiresComment: wo?.requiresComment ?? false,
        requiresConditionUpdate: wo?.requiresConditionUpdate ?? false,
        componentKeys,
        afterPhotoCount: afterPhotos.length,
    };

    const handleComplete = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setCompletionError(null);
        const conditions: Record<string, ConditionUpdatePayload> = {};
        Object.entries(draft.values.conditions).forEach(([key, c]) => {
            if (c.value !== null) conditions[key] = { value: c.value, comment: c.comment?.trim() || undefined };
        });
        const comment = draft.values.comment.trim();
        try {
            await completeWorkOrder(comment || undefined, Object.keys(conditions).length > 0 ? conditions : undefined);
            setLocalCompletion({ completedAt: formatDateForStorage(new Date()), comment: comment || undefined, conditions });
            draft.clear();
        } catch (err) {
            // Read the message from the thrown error, not from hook state (stale closure).
            setCompletionError(err instanceof Error ? err.message : 'Kunde inte slutföra ärendet');
        } finally {
            submittingRef.current = false;
        }
    };

    if (refusal) {
        return <GuestLinkRefusalPage token={token} refusal={refusal} />;
    }

    if (!wo || !building) {
        return (
            <div className="guest-page">
                <div className="guest-page__header"><img src={logoWhite} alt="Molnfastigheter" className="guest-page__header-logo" /></div>
                <Container className="guest-page__container"><Alert variant="danger">{error || 'Kunde inte ladda ärendet'}</Alert></Container>
            </div>
        );
    }

    // After a submit the page stays and shows the read-only "Slutförande" section.
    const completedAt = localCompletion?.completedAt ?? wo.completedAt ?? '';
    const completionComment = localCompletion?.comment ?? wo.completionComment;
    const completionConditions = localCompletion?.conditions
        ? Object.fromEntries(Object.entries(localCompletion.conditions).map(([k, c]) => [k, { value: c.value, comment: c.comment }]))
        : wo.completionConditions;
    const state = localCompletion ? 'completed' : wo.status;
    const addressLines = [building.street, [building.postalCode, building.city].filter(Boolean).join(' ')].filter((l): l is string => !!l);

    const photoGroup = (type: PhotoType, title: string, photos: PhotoGroupItem[], source?: { [key: string]: PhotoModel }, id?: string) => (
        <div id={id} key={type}>
            <PhotoGroup
                title={title}
                photos={photos}
                onAdd={isOpen ? () => photoInputs.current[type]?.click() : undefined}
                onRemove={isOpen ? (key) => { void deletePhoto(type, key); } : undefined}
                onOpen={openPhoto(photos, source)}
            />
            {isOpen && (
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    ref={(el) => { photoInputs.current[type] = el; }}
                    onChange={(e) => { void handlePhotoFiles(type, e.target.files); e.target.value = ''; }}
                />
            )}
        </div>
    );

    return (
        <div className="guest-page">
            <div className="guest-page__header">
                <img src={logoWhite} alt="Molnfastigheter" className="guest-page__header-logo" />
            </div>
            {/* The pill bar, as the admin modal's title bar */}
            <div className="guest-page__titlebar">
                <div className="guest-page__titlebar-inner">
                    <WorkOrderBadges priority={toPriority(wo.priority)} status={toStatus(state)} dueDate={wo.dueDate} />
                </div>
            </div>
            <Container className="guest-page__container">
                {back && (
                    // Own row: the link and the autosave line are both inline-flex
                    <div className="mb-3">
                        <Link to={back.href} className="guest-page__back d-inline-flex align-items-center gap-1">
                            <ArrowLeft size={16} /> {back.label}
                        </Link>
                    </div>
                )}
                {error && <Alert variant="danger" dismissible onClose={clearError}>{error}</Alert>}
                {isOpen && <AutosaveIndicator state={draft.autosave} className="mb-2" />}

                {/* 1–3: title, building and description (shared with the admin modal) */}
                <WorkOrderTop
                    header={<WorkOrderHeader title={wo.title} priority={toPriority(wo.priority)} status={toStatus(state)} dueDate={wo.dueDate} showBadges={false} />}
                    building={
                        <BuildingCard
                            name={building.name}
                            addressLines={addressLines}
                            coverPhotoUrl={coverPhoto.url || undefined}
                            directionsUrl={building.navigationUrl || undefined}
                        />
                    }
                    description={wo.description}
                    createdAt={wo.createdAt || ''}
                />

                {/* 4 Detaljer — no names: the guest payload carries no user data */}
                <DetailsList
                    createdAt={wo.createdAt || ''}
                    showCreated={false}
                    dueDate={wo.dueDate}
                    overdue={isWorkOrderOverdue(toStatus(state), wo.dueDate)}
                    projectLabel={wo.projectTitle}
                    unitLabel={wo.unitLabel}
                    isOpen={isOpen}
                    internalAssignees={[]}
                    externalAssignees={[{ key: currentUid || 'self', name: 'Delegerad till dig' }]}
                />

                {/* 5 Komponenter + Hitta komponenterna */}
                <GuestComponentLocations
                    componentLocations={wo.componentLocations}
                    componentNames={wo.componentNames}
                    componentKeys={componentKeys}
                    onGetDownloadUrl={getDownloadUrl}
                />

                {/* 7 Foton från felanmälan */}
                {issuePhotos.length > 0 && <IssuePhotosSection photos={issuePhotos} onOpen={openPhoto(issuePhotos, wo.sourcePhotos)} />}

                {/* 8 Bilagor */}
                <AttachmentsList
                    attachments={attachmentRows}
                    onUpload={isOpen ? (files) => { void handleUploadAttachments(files); } : undefined}
                    uploading={uploadingAttachments}
                    onRemove={isOpen ? (key) => { void deleteAttachment(key); } : undefined}
                    onOpen={(key) => { void handleOpenAttachment(key); }}
                    error={attachmentError}
                />

                {/* 9 Dokumentation */}
                <DocumentationSection>
                        {sections.notes && (
                            <NotesSection
                                notes={Object.values(project?.notes || {})}
                                currentUserId={currentUid}
                                canEdit={isOpen}
                                onAdd={(text) => addNote('', text)}
                                onUpdate={(noteKey, text) => updateNote(noteKey, '', text)}
                                onDelete={deleteNote}
                            />
                        )}
                        {sections.checklists && (
                            <ChecklistsSection
                                checklists={Object.values(project?.checklists || {})}
                                currentUserId={currentUid}
                                canEdit={isOpen}
                                onAddChecklist={(title) => createChecklist(title)}
                                onDeleteChecklist={deleteChecklist}
                                onAddItem={addChecklistItem}
                                onToggleItem={setChecklistItemCompleted}
                                onDeleteItem={deleteChecklistItem}
                            />
                        )}
                        {sections.beforePictures && photoGroup('before', L.beforePictures, beforePhotos, project?.beforePictures)}
                        {sections.progressPictures && photoGroup('progress', L.progressPictures, progressPhotos, project?.progressPictures)}
                        {photoGroup('after', L.afterPictures, afterPhotos, project?.afterPictures, AFTER_PHOTOS_ID)}
                    </DocumentationSection>

                {/* 10 Fel hittade under arbetet — open orders only */}
                {isOpen && (
                    <ReportedIssuesSection
                        onReport={() => setShowReportIssue(true)}
                        ownReportCount={ownReportCount}
                    />
                )}

                {/* 11 Slutför ärende / Slutförande */}
                {isOpen ? (
                    <CompletionForm
                        values={draft.values}
                        onChange={draft.setValues}
                        rules={rules}
                        components={componentKeys.map((key) => ({ key, name: wo.componentNames[key] || 'Komponent', expectedLifetimeYears: null }))}
                        showCost={false}
                        onViewComponent={(key) => document.getElementById(`wo-component-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        onSubmit={handleComplete}
                        submitting={submitting}
                        error={completionError}
                    />
                ) : state === 'completed' ? (
                    <CompletionSummary
                        completedAt={completedAt}
                        comment={completionComment}
                        conditions={completionConditions}
                        componentNames={wo.componentNames}
                        showCost={false}
                    />
                ) : null}

                <div className="guest-page__footer">
                    <small className="text-muted">Powered by Molnfastigheter Sverige AB</small>
                </div>
            </Container>

            <PhotoViewerModal {...photoViewer.viewerProps} />
            <GuestReportIssueModal
                show={showReportIssue}
                onHide={() => setShowReportIssue(false)}
                components={componentKeys.map((key) => ({ key, name: wo.componentNames[key] || 'Komponent' }))}
                submitting={reportIssue.submitting}
                onSubmit={async (report) => {
                    await reportIssue.report(report);
                    setOwnReportCount((n) => n + 1);
                }}
            />
        </div>
    );
};

export default GuestTaskPage;
